// ============================================================
// TargetScanHud - the scan's status banner (v8.265.0).
//
// Round 3 of the playtest calmed the HUD down: the ECG, the trauma
// ladder, the gauges and the soak console are gone (the wounds
// readout below the map already tells the damage story, and soak /
// force field are header fact-columns). What remains is ONE slim
// banner across the top of the scan: dodge and parry with their
// exact odds, then cover / stance / facing - lamp grammar, always
// on, dim rather than absent. Pointer-events none throughout, the
// TargetFrame's rule.
// ============================================================
import { deriveDefenceLedger } from '../engine/defenceLedger';
import type { DefenceLedgerOptions } from '../engine/defenceLedger';
import { effectiveVehicleFacing, VEHICLE_FACING_LABELS } from '../engine/vehicleFacing';
import { getBodyConfig } from '../engine/bodyConfig';
import { condType } from '../engine/conditions';
import clsx from '../utils/clsx';
import type { Target } from '../types';
import styles from '../styles/TargetScanHud.module.css';

interface Props {
  target: Target;
  ledgerOptions?: DefenceLedgerOptions;
}

const pct = (p: number) => `${Math.round(p * 100)}%`;

const COVER_WORDS: Record<string, string> = { low: 'LOW', half: 'HALF', threeQuarter: '¾', full: 'FULL' };

/** One indicator lamp: lit with its value, or dim - never absent. */
function Lamp({ id, label, value, title }: { id: string; label: string; value: string | null; title?: string }) {
  return (
    <span
      className={clsx(styles.lamp, value && styles.lampLit)}
      data-testid={`hud-lamp-${id}`}
      data-lit={!!value}
      title={title}
    >
      <span className={styles.lampDot} aria-hidden="true" />
      {label}
      {value ? (
        <>
          {' '}
          <span className={styles.lampValue}>{value}</span>
        </>
      ) : null}
    </span>
  );
}

type LedgerEntry = ReturnType<typeof deriveDefenceLedger>['entries'][number];

/**
 * A reaction's lamp: its EXACT odds, or dim - blocked or untrained - with
 * a title naming the reason.
 */
function ReactionLamp({ id, label, entry }: { id: string; label: string; entry: LedgerEntry | undefined }) {
  const value = entry && !entry.blockedBy ? pct(entry.odds ?? 0) : null;
  return <Lamp id={id} label={label} value={value} title={entry?.blockedBy?.join(', ')} />;
}

/** "¾ 4" when in cover, else null. */
function coverValue(target: Target): string | null {
  const level = target.cover?.level;
  const word = level && level !== 'none' ? COVER_WORDS[level] : null;
  return word ? `${word} ${target.cover?.ap ?? 0}` : null;
}

export default function TargetScanHud({ target, ledgerOptions = {} }: Props) {
  const { entries } = deriveDefenceLedger(target, ledgerOptions);
  const config = getBodyConfig(target.bodyType || (target.horde?.isHorde ? 'horde' : 'humanoid'));
  const stanceOn = (target.conditions || []).some((c) => condType(c) === 'Ds');
  return (
    <div className={styles.hud} data-testid="target-scan-hud" aria-hidden="true">
      <div className={clsx(styles.block, styles.tc)} data-testid="hud-lamps">
        <ReactionLamp id="dodge" label="DODGE" entry={entries.find((e) => e.key === 'dodge')} />
        <ReactionLamp id="parry" label="PARRY" entry={entries.find((e) => e.key === 'parry')} />
        <Lamp id="cover" label="COVER" value={coverValue(target)} />
        <Lamp id="stance" label="STANCE" value={stanceOn ? 'SET' : null} />
        {config.armourMode === 'facing' && (
          <Lamp
            id="facing"
            label="FACING"
            value={(VEHICLE_FACING_LABELS[effectiveVehicleFacing(target) ?? 'front'] || '').toUpperCase()}
          />
        )}
      </div>
    </div>
  );
}
